/**
 * Configuration utility for managing app settings from environment variables
 */

export interface AppConfig {
  pricing: {
    adPrice: number;
    adPriceFormatted: string;
    adPriceCents: number;
  };
  currency: {
    code: string;
    symbol: string;
  };
}

/**
 * Get the ad price from environment variables with fallback
 */
const getAdPrice = (): number => {
  const envPrice = process.env.REACT_APP_AD_PRICE;
  if (envPrice) {
    const parsed = parseFloat(envPrice);
    if (!isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }
  // Fallback to default price
  return 49.99;
};

/**
 * Format price as currency string
 */
const formatPrice = (price: number): string => {
  return `$${price.toFixed(2)}`;
};

/**
 * Convert price to cents for payment processors like Stripe
 */
const priceToCents = (price: number): number => {
  return Math.round(price * 100);
};

/**
 * Get complete app configuration
 */
export const getAppConfig = (): AppConfig => {
  const adPrice = getAdPrice();
  
  return {
    pricing: {
      adPrice,
      adPriceFormatted: formatPrice(adPrice),
      adPriceCents: priceToCents(adPrice)
    },
    currency: {
      code: 'USD',
      symbol: '$'
    }
  };
};

/**
 * Singleton config instance
 */
export const appConfig = getAppConfig();

/**
 * Quick access utilities
 */
export const { pricing: priceConfig, currency: currencyConfig } = appConfig;

// Export individual pricing values for convenience
export const AD_PRICE = appConfig.pricing.adPrice;
export const AD_PRICE_FORMATTED = appConfig.pricing.adPriceFormatted;
export const AD_PRICE_CENTS = appConfig.pricing.adPriceCents; 